import React from 'react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { api } from '../utils/api';
import { FASTIFY_BASE_URL } from '../config/apiConfig';

/**
 * Data hooks for the native report pages — POST /reportData/<endpoint>
 * via the shared `api` axios instance (auth headers handled by its
 * interceptor), cached with @tanstack/react-query.
 *
 * Request/response envelopes per design-scratch/pbi-extract/
 * backend-implementation-plan.md §7: requests carry { organizationId,
 * locationIds, startDate, endDate, granularity, filters, page, pageSize,
 * sort, targetCurrency, conversionMode, bypassCache }; responses are
 * { success, data, metadata }.
 *
 * The app has no global QueryClientProvider, so these hooks pass a
 * module-level QueryClient directly to useQuery (supported in
 * @tanstack/react-query v5) — no provider wiring required. A
 * <ReportQueryProvider> is still exported for pages that want devtools
 * or their own scope.
 */

/**
 * Query keys fetched since the last hard page load (module scope resets on
 * full navigation). The server-side ReportCache (reportData/index.js) serves
 * entries up to 10 minutes old and data ingestion doesn't reliably bump its
 * version key, so the first fetch after a page load must not TRUST it —
 * but it used to BYPASS it (bypassCache on every first fetch), which made
 * every page open pay full cold-SQL latency precisely while the user
 * watches a skeleton. Now the first fetch is stale-while-revalidate:
 * answer from the server cache immediately, then re-post with bypassCache
 * in the background and swap the fresh result in (react-query re-renders;
 * the charts tween). Freshness is preserved, first paint is a cache hit.
 */
const fetchedSincePageLoad = new Set();
const revalidatingKeys = new Set();

function revalidateInBackground(endpoint, params, key) {
  if (revalidatingKeys.has(key)) return;
  revalidatingKeys.add(key);
  api
    .post(`${FASTIFY_BASE_URL}/reportData/${endpoint}`, { ...params, bypassCache: true })
    .then((res) => {
      if (res.data && res.data.success !== false) {
        reportQueryClient.setQueryData(['reportData', endpoint, stableStringify(params || {})], res.data);
      }
    })
    .catch(() => {
      /* background refresh is best-effort; the visible data came from the
         server cache and the next user action refetches normally */
    })
    .finally(() => {
      revalidatingKeys.delete(key);
    });
}

export const reportQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // server-side ReportCache TTL is 10 min
      gcTime: 15 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

export function ReportQueryProvider({ children }) {
  return <QueryClientProvider client={reportQueryClient}>{children}</QueryClientProvider>;
}

/** Deterministic key for params objects (insertion-order independent). */
function stableStringify(value) {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  return `{${Object.keys(value)
    .sort()
    .filter((k) => value[k] !== undefined)
    .map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`)
    .join(',')}}`;
}

function extractError(err) {
  const serverMessage = err?.response?.data?.error || err?.response?.data?.message;
  const e = new Error(serverMessage || err?.message || 'Report request failed');
  e.status = err?.response?.status;
  e.cause = err;
  return e;
}

/**
 * useReportData('financialPnl', params, { enabled })
 *
 * params  — request envelope (must include organizationId; the query
 *           stays idle until it does)
 * options — { enabled, keepPreviousData (default true — old rows stay
 *           on screen while filters refetch), staleTime, refetchInterval }
 *
 * Returns { data, metadata, isLoading, isFetching, error, refetch }
 *   data/metadata are the response envelope fields; refetch({ bypassCache })
 *   re-posts with bypassCache for the page-level Refresh button.
 */
/** Query key + fetcher shared by useReportData and prefetchReportData. */
function reportQuery(endpoint, params) {
  return {
    queryKey: ['reportData', endpoint, stableStringify(params || {})],
    queryFn: async () => {
      const fetchId = `${endpoint}|${stableStringify(params || {})}`;
      const firstFetchThisPageLoad = !fetchedSincePageLoad.has(fetchId);
      try {
        const res = await api.post(`${FASTIFY_BASE_URL}/reportData/${endpoint}`, params);
        if (res.data && res.data.success === false) {
          throw new Error(res.data.error || `Report request failed: ${endpoint}`);
        }
        fetchedSincePageLoad.add(fetchId);
        // First view this page load AND the server answered from cache:
        // kick off a background bypassCache re-fetch so any post-ingestion
        // drift corrects itself seconds later without blocking first paint.
        if (firstFetchThisPageLoad && res.data?.metadata?.fromCache === true) {
          revalidateInBackground(endpoint, params, fetchId);
        }
        return res.data;
      } catch (err) {
        throw err instanceof Error && !err.response ? err : extractError(err);
      }
    },
  };
}

/**
 * Warm the cache for a query a page is likely to ask for next (e.g. the
 * dashboard's per-property scopes). No-op when fresh data is already cached;
 * failures are swallowed — the real query retries when it mounts.
 */
export function prefetchReportData(endpoint, params) {
  if (!endpoint || !params?.organizationId) return Promise.resolve();
  return reportQueryClient.prefetchQuery(reportQuery(endpoint, params)).catch(() => {});
}

export function useReportData(endpoint, params, options = {}) {
  const { enabled = true, keepPreviousData = true, staleTime, refetchInterval } = options;

  const query = useQuery(
    {
      ...reportQuery(endpoint, params),
      enabled: Boolean(enabled && endpoint && params && params.organizationId),
      placeholderData: keepPreviousData ? (prev) => prev : undefined,
      ...(staleTime !== undefined ? { staleTime } : {}),
      ...(refetchInterval !== undefined ? { refetchInterval } : {}),
    },
    reportQueryClient
  );

  const refetch = React.useCallback(
    async ({ bypassCache = false } = {}) => {
      if (bypassCache) {
        // one-shot bypass: post directly, then seed the cache with the result
        const res = await api.post(`${FASTIFY_BASE_URL}/reportData/${endpoint}`, { ...params, bypassCache: true });
        if (res.data && res.data.success === false) {
          throw new Error(res.data.error || `Report request failed: ${endpoint}`);
        }
        fetchedSincePageLoad.add(`${endpoint}|${stableStringify(params || {})}`);
        reportQueryClient.setQueryData(['reportData', endpoint, stableStringify(params || {})], res.data);
        return res.data;
      }
      const result = await query.refetch();
      return result.data;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [endpoint, stableStringify(params || {}), query.refetch]
  );

  return {
    data: query.data?.data,
    metadata: query.data?.metadata,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error || null,
    refetch,
  };
}

/**
 * useReportFreshness(orgId) — GET /reportData/freshness for the
 * "Data refreshed …" indicator. Refetches every 5 minutes by default.
 *
 * Returns { freshness (raw payload), lastRefreshedAt (best-effort Date
 * string for the banner), isLoading, error, refetch }.
 */
export function useReportFreshness(organizationId, options = {}) {
  const { enabled = true, refetchInterval = 5 * 60 * 1000 } = options;

  const query = useQuery(
    {
      queryKey: ['reportData', 'freshness', organizationId],
      queryFn: async () => {
        try {
          const res = await api.get(`${FASTIFY_BASE_URL}/reportData/freshness`, { params: { organizationId } });
          if (res.data && res.data.success === false) {
            throw new Error(res.data.error || 'Freshness request failed');
          }
          return res.data;
        } catch (err) {
          throw err instanceof Error && !err.response ? err : extractError(err);
        }
      },
      enabled: Boolean(enabled && organizationId),
      refetchInterval,
      staleTime: 60 * 1000,
    },
    reportQueryClient
  );

  const payload = query.data?.data ?? query.data;
  const lastRefreshedAt =
    payload?.dashboardRefreshLastRunAt ||
    payload?.lastRefreshedAt ||
    (Array.isArray(payload?.facts)
      ? payload.facts.reduce((max, f) => (f.lastRefreshedAt > max ? f.lastRefreshedAt : max), '')
      : null) ||
    null;

  return {
    freshness: payload || null,
    lastRefreshedAt: lastRefreshedAt || null,
    isLoading: query.isLoading,
    error: query.error || null,
    refetch: query.refetch,
  };
}

export default useReportData;
